import { useMemo, useState } from 'react';
import { Button, GenericError, Dialog, PageLoader } from '../../common';
import VoteEventTeams from './VoteEventTeams';
import { IEvent, TeamUser, UserRating } from '../../../utils/types';
import { useVoteEvent } from './useVoteEvent';

import './voteEvent.css';
import { useTranslation } from '../../../hooks/translations/TranslationProvider';

interface VoteEventProps {
    eventDetails: IEvent;
    isLoading: boolean;
    foundUserInTeam?: TeamUser;
    onUserReport: () => void;
    refreshEventData: () => void;
}

export default function VoteEvent({
    eventDetails,
    isLoading,
    foundUserInTeam,
    onUserReport,
    refreshEventData,
}: VoteEventProps) {
    const { t } = useTranslation();

    const { error, voteEvent, isVoteLoading } = useVoteEvent(eventDetails.id);

    const initialRatingsValue = useMemo(() => {
        const userIds = eventDetails.teams.flatMap(team =>
            team.users
                .filter(
                    user =>
                        user.id !== foundUserInTeam?.id &&
                        !eventDetails.reports.includes(user.id)
                )
                .map(user => user.id)
        );

        return userIds.map(id => {
            return {
                userId: id,
                rating: 3,
            };
        });
    }, [eventDetails]);

    const [winner, setWinner] = useState<string | null>(null);
    const [ratings, setRatings] = useState<UserRating[]>(initialRatingsValue);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const hasUserVoted = foundUserInTeam?.hasVoted;
    const showConfirmVoteButton = !hasUserVoted;

    const onMarkAsWinner = (teamId: string) => {
        setWinner(teamId);
    };

    const onConfirmVoteHandler = async () => {
        await voteEvent(ratings, winner);
        refreshEventData();
    };

    function onRatingChange(newRatings: UserRating[]) {
        setRatings(newRatings);
    }

    if (isLoading || !eventDetails || isVoteLoading) {
        return <PageLoader />;
    }

    if (error) {
        return <GenericError error={error} />;
    }

    return (
        <div className="vote-event-teams-wrapper">
            <VoteEventTeams
                eventDetails={eventDetails}
                ratings={ratings}
                winner={winner}
                onMarkAsWinner={onMarkAsWinner}
                onRatingChange={onRatingChange}
                foundUserInTeam={foundUserInTeam}
                onUserReport={onUserReport}
            />
            {showConfirmVoteButton && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                        text={t('eventDetails.confirmVote')}
                        size="md"
                        variant="green"
                        onClick={() => setShowConfirmDialog(true)}
                    />
                </div>
            )}
            <Dialog
                isOpen={showConfirmDialog}
                title={t('eventDetails.confirmVoteDialog.title')}
                bodyText={t('eventDetails.confirmVoteDialog.content')}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={onConfirmVoteHandler}
                isLoading={isLoading}
            />
        </div>
    );
}
